import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  ApiError,
  setAccessToken,
  setStoredRefreshToken,
  getStoredRefreshToken,
  tryRefresh,
} from "../../lib/api";
import { fetchMe, login as loginRequest, type SafeUser } from "./api";

interface AuthValue {
  user: SafeUser | null;
  loading: boolean;
  login: (email: string, password: string, totp?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SafeUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (getStoredRefreshToken() && (await tryRefresh())) {
          const { user } = await fetchMe();
          if (!cancelled && user.role === "admin") setUser(user);
        }
      } catch {
        setStoredRefreshToken(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string, totp?: string) => {
    const bundle = await loginRequest({ email, password, totp });
    if (bundle.user.role !== "admin") {
      setAccessToken(null);
      throw new ApiError(403, "forbidden", "Ce compte n’est pas administrateur");
    }
    setAccessToken(bundle.accessToken);
    setStoredRefreshToken(bundle.refreshToken);
    setUser(bundle.user);
  }, []);

  const logout = useCallback(() => {
    setAccessToken(null);
    setStoredRefreshToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const { user } = await fetchMe();
    setUser(user);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshUser }),
    [user, loading, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth hors AuthProvider");
  return ctx;
}
